import { useCallback, useLayoutEffect, useRef, useState, type PointerEvent, type KeyboardEvent } from "react";
import { useDraft } from "../lib/drafts";

export type ChatPosition = "left" | "right" | "top" | "bottom" | "floating";
export function useConversationLayout() {
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const ref = useCallback((node: HTMLDivElement | null) => setElement(node), []);
  const [storedPosition, setPosition] = useDraft<ChatPosition>("chat:position", "right");
  const position = ["left", "right", "top", "bottom", "floating"].includes(storedPosition) ? storedPosition : "right";
  const [savedWidth, setWidth] = useDraft("chat:width", 400);
  const [savedHeight, setHeight] = useDraft("chat:height", 320);
  const [savedFloating, setFloating] = useDraft<null | FloatingRect>("chat:floating", null);
  const gesture = useRef<null | { kind: "move" | "resize"; x: number; y: number; rect: FloatingRect }>(null);
  const [bounds, setBounds] = useState({ width: window.innerWidth, height: window.innerHeight });
  const [dragging, setDragging] = useState(false);
  useLayoutEffect(() => {
    if (!element) return;
    const measure = () => {
      const rect = element.getBoundingClientRect();
      if (rect.width && rect.height) setBounds(current => current.width === rect.width && current.height === rect.height ? current : { width: rect.width, height: rect.height });
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);
  const vertical = position === "top" || position === "bottom";
  const maxWidth = Math.max(280, bounds.width - 241);
  const maxHeight = Math.max(200, bounds.height - 181);
  const clamp = (value: number, min: number, max: number, fallback: number) => Math.max(min, Math.min(max, Number.isFinite(value) ? value : fallback));
  const width = clamp(savedWidth, 280, maxWidth, 400);
  const height = clamp(savedHeight, 200, maxHeight, 320);
  const min = vertical ? 200 : 280, max = vertical ? maxHeight : maxWidth;
  const size = vertical ? height : width;
  const resize = (value: number) => (vertical ? setHeight : setWidth)(clamp(value, min, max, size));
  const leading = position === "left" || position === "top";
  const fitFloating = (rect: Partial<FloatingRect> | null): FloatingRect => {
    const availableWidth = Math.max(1, bounds.width - 24), availableHeight = Math.max(1, bounds.height - 24);
    const width = clamp(rect?.width ?? 420, Math.min(280, availableWidth), availableWidth, 420);
    const height = clamp(rect?.height ?? 480, Math.min(220, availableHeight), availableHeight, 480);
    return { width, height,
      x: clamp(rect?.x ?? bounds.width - width - 16, 12, Math.max(12, bounds.width - width - 12), 12),
      y: clamp(rect?.y ?? bounds.height - height - 16, 12, Math.max(12, bounds.height - height - 12), 12),
    };
  };
  const floating = fitFloating(savedFloating);
  const updateFloating = (kind: "move" | "resize", rect: FloatingRect, dx: number, dy: number) => {
    if (kind === "move") setFloating(fitFloating({ ...rect, x: rect.x + dx, y: rect.y + dy }));
    else setFloating(fitFloating({ ...rect,
      width: Math.min(bounds.width - rect.x - 12, Math.max(280, rect.width + dx)),
      height: Math.min(bounds.height - rect.y - 12, Math.max(220, rect.height + dy)),
    }));
  };
  const floatingProps = (kind: "move" | "resize") => ({
    onPointerDown(event: PointerEvent<HTMLButtonElement>) {
      if (event.button !== 0) return;
      event.currentTarget.focus();
      event.currentTarget.setPointerCapture(event.pointerId);
      gesture.current = { kind, x: event.clientX, y: event.clientY, rect: floating };
      setDragging(true); event.preventDefault();
    },
    onPointerMove(event: PointerEvent<HTMLButtonElement>) {
      const start = gesture.current;
      if (!start || !event.currentTarget.hasPointerCapture(event.pointerId)) return;
      updateFloating(start.kind, start.rect, event.clientX - start.x, event.clientY - start.y);
    },
    onPointerUp(event: PointerEvent<HTMLButtonElement>) {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
      gesture.current = null; setDragging(false);
    },
    onPointerCancel() { gesture.current = null; setDragging(false); },
    onLostPointerCapture() { gesture.current = null; setDragging(false); },
    onKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
      if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
      event.preventDefault();
      const step = event.shiftKey ? 40 : 12;
      updateFloating(kind, floating, event.key === "ArrowLeft" ? -step : event.key === "ArrowRight" ? step : 0,
        event.key === "ArrowUp" ? -step : event.key === "ArrowDown" ? step : 0);
    },
  });
  return { ref, position, setPosition, width, height, vertical, min, max, size, dragging, gestureKind: gesture.current?.kind, floating, moveProps: floatingProps("move"), resizeProps: floatingProps("resize"),
    dividerProps: {
      onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
        const backward = vertical ? "ArrowUp" : "ArrowLeft", forward = vertical ? "ArrowDown" : "ArrowRight";
        if (![backward, forward, "Home", "End"].includes(event.key)) return;
        event.preventDefault();
        resize(event.key === "Home" ? min : event.key === "End" ? max : size + (event.key === forward ? 24 : -24) * (leading ? 1 : -1));
      },
      onPointerDown(event: PointerEvent<HTMLDivElement>) {
        if (event.button !== 0) return;
        event.currentTarget.setPointerCapture(event.pointerId); setDragging(true); event.preventDefault();
      },
      onPointerMove(event: PointerEvent<HTMLDivElement>) {
        if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
        const rect = element!.getBoundingClientRect();
        resize(vertical ? (leading ? event.clientY - rect.top : rect.bottom - event.clientY) : (leading ? event.clientX - rect.left : rect.right - event.clientX));
      },
      onPointerUp(event: PointerEvent<HTMLDivElement>) {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
        setDragging(false);
      },
      onPointerCancel() { setDragging(false); },
      onLostPointerCapture() { setDragging(false); },
      onDoubleClick() { resize(vertical ? 320 : 400); },
    },
  };
}

interface FloatingRect { x: number; y: number; width: number; height: number }
